import { createRouter, createWebHistory } from 'vue-router'
import Home from '../features/Home.vue'
import { useRootStore } from '@/stores/store'
import { getUser } from '@/api/api.js'
import { useHead } from '@unhead/vue'

import ProductOrder from '../features/productOrder/ProductOrder.vue'

import FileStore from '../features/filestore/FileStore.vue'
import FileStoreHome from '../features/filestore/FileStoreHome.vue'
import Trash from '../features/filestore/children/Trash.vue'
import Dashboard from '../features/filestore/children/Dashboard.vue'
import Header from '../features/filestore/children/Header.vue'
import SearchResults from '../features/filestore/children/SearchResults.vue'
import SearchResultsHeader from '../features/filestore/children/SearchResultsHeader.vue'
import FileBrowser from '../features/filestore/children/FileBrowser.vue'
import ArchiveHeader from '../features/filestore/children/ArchiveHeader.vue'
import TrashHeader from '@/features/filestore/children/TrashHeader.vue'
import TrashArchiveList from '@/features/filestore/children/TrashArchiveList.vue'

import PrintStore from '@/features/printstore/PrintStore.vue'
import PrintStoreDashboard from '@/features/printstore/children/Dashboard.vue'
import PrintStoreCustomIntro from '@/features/printstore/children/CustomIntroPage.vue'
import PrintStoreCategory from '@/features/printstore/children/Category.vue'
import PrintStoreCollection from '@/features/printstore/children/Collection.vue'
import PrintStoreProduct from '@/features/printstore/children/Product.vue'
import PrintStorePackage from '@/features/printstore/children/Package.vue'
import { default as PrintStoreSearchResults } from '@/features/printstore/children/SearchResults.vue'

import PrintOrder from '../features/printOrder/PrintOrder.vue'
import PrintOrderCart from '../features/printOrder/children/Cart.vue'
import PrintOrderCartV2 from '../features/printOrder/children/CartV2.vue'
import PrintOrderDeliver from '../features/printOrder/children/Deliver.vue'
import PrintOrderInvoice from '../features/printOrder/children/Invoice.vue'
import PrintOrderConfirm from '../features/printOrder/children/Confirm.vue'

import ScanOrder from '@/features/scanOrder/ScanOrder.vue'
import ScanOrderPickUp from '@/features/scanOrder/children/PickUp'
import ScanOrderDeliver from '@/features/scanOrder/children/Deliver'
import ScanOrderInvoice from '@/features/scanOrder/children/Invoice'
import ScanOrderConfirm from '@/features/scanOrder/children/Confirm'

import CopyOrder from '../features/copyOrder/CopyOrder.vue'
import CopyOrderPickUp from '@/features/copyOrder/children/PickUp'
import CopyOrderDeliver from '@/features/copyOrder/children/Deliver'
import CopyOrderInvoice from '@/features/copyOrder/children/Invoice'
import CopyOrderConfirm from '@/features/copyOrder/children/Confirm'

import CheckOut from '../features/checkOut/CheckOut.vue'
import CheckOutDeliver from '@/features/checkOut/children/Bezorgen.vue'
import CheckOutInvoice from '@/features/checkOut/children/Factureren.vue'
import CheckOutConfirm from '@/features/checkOut/children/Confirm.vue'
import CheckOutCart from '@/features/checkOut/children/Cart.vue'

import QuoteOrder from '../features/quoteOrder/QuoteOrder.vue'
import QuoteOrderDescription from '@/features/quoteOrder/children/Description.vue'
import QuoteOrderDeliver from '@/features/quoteOrder/children/Deliver.vue'
import QuoteOrderInvoice from '@/features/quoteOrder/children/Invoice.vue'
import QuoteOrderConfirm from '@/features/quoteOrder/children/Confirm.vue'

import PrintProductSecuredSA from '../features/printProductSecuredSA/PrintProductSecuredSA.vue'
import PrintProductSecuredSACart from '@/features/printProductSecuredSA/children/Cart.vue'
import PrintProductSecuredSADeliver from '@/features/printProductSecuredSA/children/Deliver.vue'
import PrintProductSecuredSAInvoice from '@/features/printProductSecuredSA/children/Invoice.vue'
import PrintProductSecuredSAConfirm from '@/features/printProductSecuredSA/children/Confirm.vue'

function parseFileBrowserProps(route) {
  let result = {}

  if (route.params.archiveId) {
    result.archiveId = parseInt(route.params.archiveId)
  }

  if (route.params.folderId) {
    result.folderId = parseInt(route.params.folderId)
  }

  return result
}

const routes = [
  {
    path: '/wat-wij-doen',
    name: 'wat-wij-doen',
    component: () => import('../features/pages/WatWijDoen.vue'),
    meta: {
      title: 'Wat wij doen | Koninklijke Rijnja',
      description:
        'Vanuit 5 locaties verspreid over het land verzorgen we zo’n 160.000 printopdrachten per jaar. '
    }
  },
  {
    path: '/print-uitbesteden',
    name: 'print-uitbesteden',
    component: () => import('../features/pages/PrintUitbesteden.vue'),
    meta: {
      title: 'Print uitbesteden | Koninklijke Rijnja',
      description:
        'Professioneel printen uitbesteden betekent besparen op fysieke ruimte van een reproafdeling en alle vaste kosten die met printen te maken hebben.'
    }
  },
  {
    path: '/print-aanbesteden',
    name: 'print-aanbesteden',
    component: () => import('../features/pages/PrintAanbesteden.vue'),
    meta: {
      title: 'Print aanbesteden | Koninklijke Rijnja',
      description:
        'Wij voeren alle printopdrachten uit in eigen beheer, van voorbereiding tot de distributie, met eigen koeriers en eigen vervoer.'
    }
  },
  {
    path: '/secured-printing',
    name: 'secured-printing',
    component: () => import('../features/pages/SecuredPrinting.vue'),
    meta: {
      title:
        'Secured printing | Veilig een bestelling plaatsen | Koninklijke Rijnja',
      description:
        'Wil je documenten beveiligd laten printen? Dan ben je bij Rijnja aan het juiste adres. Wij begrijpen dat informatiebeveiliging juist in deze tijd van groot belang is.'
    }
  },
  {
    path: '/digitaliseren',
    name: 'digitaliseren',
    component: () => import('../features/pages/Digitaliseren.vue'),
    meta: {
      title: 'Digitaliseren | Koninklijke Rijnja',
      description:
        'We digitaliseren documenten, van één rapport of bouwtekening tot een compleet archief, en leveren digitale, doorzoekbare informatie terug.'
    }
  },
  /*   {
    path: '/werken-bij-rijnja/printoperator',
    name: 'printoperator',
    component: () => import('../features/pages/PrintOperator.vue'),
    meta: {
      title: 'Werken bij Rijnja | Koninklijke Rijnja',
      description:
        'Rijnja Regelt Het voor meer dan 4000 organisaties en professionals, Regel jij het mee?'
    }
  },
  {
    path: '/werken-bij-rijnja/chauffeur',
    name: 'chauffeur',
    component: () => import('../features/pages/Chauffeur.vue'),
    meta: {
      title: 'Werken bij Rijnja | Koninklijke Rijnja',
      description:
        'Rijnja Regelt Het voor meer dan 4000 organisaties en professionals, Regel jij het mee?'
    }
  },
  {
    path: '/werken-bij-rijnja',
    name: 'werken-bij-rijnja',
    component: () => import('../features/pages/WerkenBijRijnja.vue'),
    meta: {
      title: 'Werken bij Rijnja | Koninklijke Rijnja',
      description:
        'Rijnja Regelt Het voor meer dan 4000 organisaties en professionals, Regel jij het mee?'
    }
  }, */
  {
    path: '/hoe-doen-wij-dat',
    name: 'hoe-doen-wij-dat',
    component: () => import('../features/pages/HoeDoenWijDat.vue'),
    meta: {
      title: 'Hoe doen wij dat | Koninklijke Rijnja',
      description:
        'Alle printopdrachten voeren wij in eigen beheer uit vanuit onze vijf vestigingen verspreid over het land. Onder supervisie van uw persoonlijke contactpersoon bij Rijnja, verzorgen wij uw print- en repro-opdrachten makkelijk en snel van de voorbereiding tot de distributie met eigen koeriers en levering binnen een werkdag.'
    }
  },
  {
    path: '/duurzaam',
    name: 'duurzaam',
    component: () => import('../features/pages/Duurzaam.vue'),
    meta: {
      title: 'Erkend duurzaam | Prints voor professionals | Koninklijke Rijnja',
      description:
        'Koninklijke Rijnja draagt actief bij aan een gezonde en sociale samenleving.'
    }
  },
  {
    path: '/over-rijnja',
    name: 'over-rijnja',
    component: () => import('../features/pages/OverRijnja.vue'),
    meta: {
      title:
        'Over Koninklijke Rijnja | Prints voor professionals | Koninklijke Rijnja',
      description:
        'Koninklijke Rijnja levert groot- en kleinformaat printproducten door heel Nederland. Maar we doen meer dan dat. Als grootste reprografisch dienstverlener van Nederland vinden we het belangrijk dat jouw organisatie geen enkel omkijken meer heeft naar het printwerk.'
    }
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import('../features/pages/Faq.vue'),
    meta: {
      title:
        'Veelgestelde vragen | Prints voor professionals | Koninklijke Rijnja',
      description:
        'Veel vragen zijn ons al eens gesteld. Daarom hebben we de antwoorden op de meest gestelde vragen op een rijtje gezet.'
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('../features/pages/Contact.vue'),
    meta: {
      title: 'Contact | Neem contact op met Rijnja | Koninklijke Rijnja',
      description:
        'Met vestigingen door heel Nederland zijn wij altijd in de buurt.'
    },
    props: true
  },
  {
    path: '/rijnja-api',
    name: 'rijnja-api',
    component: () => import('../features/pages/RijnjaApi.vue'),
    meta: {
      title: 'Rijnja.nl API documentation'
    }
  },
  {
    path: '/leveringsvoorwaarden',
    name: 'leveringsvoorwaarden',
    component: () => import('../features/pages/Leveringsvoorwaarden.vue'),
    meta: {
      title: 'Leveringsvoorwaarden | Koninklijke Rijnja',
      description:
        'Koninklijke Rijnja. Vroeger waren we reprograaf, nu zijn we de grootste document services provider van Nederland..'
    }
  },
  {
    path: '/cookiebeleid',
    name: 'cookiebeleid',
    component: () => import('../features/pages/CookiePolicy.vue'),
    meta: {
      title: 'Cookiebeleid | Koninklijke Rijnja',
      description:
        'Koninklijke Rijnja. Vroeger waren we reprograaf, nu zijn we de grootste document services provider van Nederland..'
    }
  },
  {
    path: '/privacyverklaring',
    name: 'privacyverklaring',
    component: () => import('../features/pages/PrivacyPolicy.vue'),
    meta: {
      title: 'Privacyverklaring | Koninklijke Rijnja',
      description:
        'Koninklijke Rijnja. Vroeger waren we reprograaf, nu zijn we de grootste document services provider van Nederland..'
    }
  },
  {
    path: '/:theme?',
    name: 'home',
    component: Home,
    beforeEnter: (to, from, next) => {
      const store = useRootStore()
      if (to.name === 'home') {
        store.globeReset()
      } else {
        store.globePlain()
      }
      next()
    },
    props: (route) => ({ dialog: route.query.dlg, addToCart: route.query['add-to-cart'] }),
    children: [
      {
        path: 'filestore',
        name: 'fileStore',
        component: FileStore,
        meta: {
          requiresLogin: true
        },
        beforeEnter: (to, from, next) => {
          const store = useRootStore()
          if (!from.name) {
            let interactions = store.interactions.filter((val) => {
              return (
                val.destination.toUpperCase() ===
                store.currentSection.toUpperCase()
              )
            })
            if (interactions.length === 1) {
              store.setInteraction(interactions[0])
            }
          }

          next()
        },
        children: [
          {
            path: '',
            name: 'fileStoreHome',
            component: FileStoreHome,
            children: [
              {
                path: 'archive/:archiveId/folder/:folderId',
                name: 'fileStoreFolder',
                components: {
                  default: FileBrowser,
                  header: ArchiveHeader
                },
                props: {
                  default: parseFileBrowserProps,
                  header: parseFileBrowserProps
                }
              },
              {
                path: 'archive/:id',
                name: 'fileStoreArchive',
                components: {
                  default: FileBrowser,
                  header: ArchiveHeader
                },
                props: {
                  default: parseFileBrowserProps,
                  header: parseFileBrowserProps
                }
              },
              {
                path: 'dashboard',
                name: 'fileStoreDashboard',
                components: {
                  default: Dashboard,
                  header: Header
                },
                meta: {
                  title: 'Dashboard'
                }
              },
              {
                path: 'trash',
                name: 'fileStoreTrashArchiveList',
                components: {
                  default: TrashArchiveList,
                  header: TrashHeader
                }
              },
              {
                path: 'trash/:folderId',
                name: 'fileStoreTrash',
                components: {
                  default: Trash,
                  header: TrashHeader
                },
                props: {
                  default: parseFileBrowserProps,
                  header: parseFileBrowserProps
                }
              },
              {
                path: 'search-results',
                name: 'fileStoreSearchResults',
                components: {
                  default: SearchResults,
                  header: SearchResultsHeader
                },
                meta: {
                  title: 'Prullenmand'
                }
              }
            ]
          }
        ]
      },
      {
        path: 'printstore',
        name: '',
        component: PrintStore,
        children: [
          {
            path: '',
            name: 'printStore',
            component: PrintStoreDashboard
          },
          {
            path: 'intro',
            name: 'printStoreIntro',
            component: PrintStoreCustomIntro,
            beforeEnter: (to, from, next) => {
              const store = useRootStore()
              if (store.theme.printStore.isDefault) {
                next('home')
              } else {
                next()
              }
            }
          },
          {
            path: 'category/:categoryId',
            name: 'printStoreCategory',
            component: PrintStoreCategory,
            props: true
          },
          {
            path: 'category/:categoryId/collection/:collectionId',
            name: 'printStoreCollection',
            component: PrintStoreCollection,
            props: true
          },
          {
            path: 'category/:categoryId/collection/:collectionId/:productId',
            name: 'printStoreProduct',
            component: PrintStoreProduct,
            props: true
          },
          {
            path: 'category/:categoryId/package/:packageId',
            name: 'printStorePackage',
            component: PrintStorePackage,
            props: true
          },
          {
            path: 'search-results',
            name: 'printStoreSearchResults',
            component: PrintStoreSearchResults
          }
        ]
      },
      {
        path: 'product-order',
        name: 'productOrder',
        props: true,
        component: ProductOrder,
        beforeEnter: (to, from, next) => {
          redirectToHomeOnRefresh(to, from, next)
        }
      },
      {
        path: 'secured-order',
        name: 'securedOrder',
        props: (route) => ({
          secure: route.query.secure === 'true' || route.query.secure === true
        }),
        component: ProductOrder,
        beforeEnter: (to, from, next) => {
          redirectToHomeOnRefresh(to, from, next)
        }
      },
      {
        path: 'print-order',
        name: '',
        props: (route) => ({
          secure: route.query.secure === 'true' || route.query.secure === true
        }),
        beforeEnter: (to, from, next) => {
          redirectToHomeOnRefresh(to, from, next)
        },
        component: PrintOrder,
        children: [
          {
            path: '',
            name: 'printOrder',
            component: PrintOrderCart,
            beforeEnter: (to, from, next) => {
              const store = useRootStore()

              if (store.theme.customFields.printOrder?.cartV2) {
                next({
                  name: 'printOrderCartV2',
                  query: to.query,
                  replace: true
                })
              } else {
                next()
              }
            }
          },
          {
            path: 'printen',
            name: 'printOrderCartV2',
            component: PrintOrderCartV2
          },
          {
            path: 'bezorgen',
            name: 'printOrderDeliver',
            component: PrintOrderDeliver
          },
          {
            path: 'factureren',
            name: 'printOrderInvoice',
            component: PrintOrderInvoice
          },
          {
            path: 'controleren',
            name: 'printOrderConfirm',
            component: PrintOrderConfirm
          }
        ]
      },
      {
        path: 'scan-order',
        name: 'digitizeOrder',
        component: ScanOrder,
        redirect: 'scan-order/ophalen',
        beforeEnter: (to, from, next) => {
          redirectToHomeOnRefresh(to, from, next)
        },
        children: [
          {
            path: 'ophalen',
            name: 'scanOrderPickUp',
            component: ScanOrderPickUp
          },
          {
            path: 'bezorgen',
            name: 'scanOrderDeliver',
            component: ScanOrderDeliver
          },
          {
            path: 'factureren',
            name: 'scanOrderInvoice',
            component: ScanOrderInvoice
          },
          {
            path: 'controleren',
            name: 'scanOrderConfirm',
            component: ScanOrderConfirm
          }
        ]
      },
      {
        path: 'kopieer-order',
        name: 'copyOrder',
        component: CopyOrder,
        redirect: 'kopieer-order/ophalen',
        beforeEnter: (to, from, next) => {
          redirectToHomeOnRefresh(to, from, next)
        },
        children: [
          {
            path: 'ophalen',
            name: 'copyOrderPickUp',
            component: CopyOrderPickUp
          },
          {
            path: 'bezorgen',
            name: 'copyOrderDeliver',
            component: CopyOrderDeliver
          },
          {
            path: 'factureren',
            name: 'copyOrderInvoice',
            component: CopyOrderInvoice
          },
          {
            path: 'controleren',
            name: 'copyOrderConfirm',
            component: CopyOrderConfirm
          }
        ]
      },
      {
        path: 'offerte-aanvraag',
        name: 'quoteOrder',
        redirect: 'offerte-aanvraag/opdracht',
        component: QuoteOrder,
        beforeEnter: (to, from, next) => {
          redirectToHomeOnRefresh(to, from, next)
        },
        children: [
          {
            path: 'opdracht',
            name: 'quoteOrderDescription',
            component: QuoteOrderDescription
          },
          {
            path: 'bezorgen',
            name: 'quoteOrderDeliver',
            component: QuoteOrderDeliver
          },
          {
            path: 'factureren',
            name: 'quoteOrderInvoice',
            component: QuoteOrderInvoice
          },
          {
            path: 'controleren',
            name: 'quoteOrderConfirm',
            component: QuoteOrderConfirm
          }
        ]
      },
      {
        path: 'check-out',
        name: 'checkOut',
        component: CheckOut,
        redirect: '/check-out/winkelwagen',
        beforeEnter: (to, from, next) => {
          redirectToHomeOnRefresh(to, from, next)
        },
        children: [
          {
            path: 'winkelwagen',
            name: 'checkOutCart',
            component: CheckOutCart
          },
          {
            path: 'bezorgen',
            name: 'checkOutDeliver',
            component: CheckOutDeliver
          },
          {
            path: 'factureren',
            name: 'checkOutInvoice',
            component: CheckOutInvoice
          },
          {
            path: 'controleren',
            name: 'checkOutConfirm',
            component: CheckOutConfirm
          }
        ]
      },
      {
        path: 'print-product-secured',
        name: '',
        component: PrintProductSecuredSA,
        beforeEnter: (to, from, next) => {
          redirectToHomeOnRefresh(to, from, next)
        },
        children: [
          {
            path: '',
            name: 'printProductSecuredSA',
            component: PrintProductSecuredSACart
          },
          {
            path: 'bezorgen',
            name: 'printProductSecuredSADeliver',
            component: PrintProductSecuredSADeliver
          },
          {
            path: 'factureren',
            name: 'printProductSecuredSAInvoice',
            component: PrintProductSecuredSAInvoice
          },
          {
            path: 'controleren',
            name: 'printProductSecuredSAConfirm',
            component: PrintProductSecuredSAConfirm
          }
        ]
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash
      }
    } else {
      document.getElementById('app')?.scrollTo(0, 0)
      return { left: 0, top: 0 }
    }
  },
  routes
})

router.beforeEach((to, from, next) => {
  // Remove trailing slashes for SEO consistency (except for home page).
  // NOTE: `replace: true` is only honoured on a router.replace() call, not on
  // the location passed to next() — without it the redirect inherits the
  // original push() and leaves an extra history entry behind, which breaks the
  // browser back button (see RNJ printstore category/collection navigation).
  if (to.path !== '/' && to.path.endsWith('/')) {
    router.replace({
      path: to.path.slice(0, -1),
      query: to.query,
      hash: to.hash
    })
    return next(false)
  }

  const store = useRootStore()

  if (!from.name && to.query['add-to-cart']) {
    console.log('add-to-cart param:', to.query['add-to-cart'])
    const query = to.query
    delete query['add-to-cart']
    to.query = query
  }

  // Determine which section we're in based on matched routes
  let currentSection = 'home'
  if (to.matched.some((route) => route.path.includes('printstore'))) {
    currentSection = 'printstore'
  } else if (to.matched.some((route) => route.path.includes('filestore'))) {
    currentSection = 'filestore'
  } else if (to.matched.some((route) => route.path.includes('printOrder'))) {
    currentSection = 'printorder'
  } else if (to.matched.some((route) => route.path.includes('checkOut'))) {
    currentSection = 'checkout'
  }
  store.currentSection = currentSection

  // new site entry, set user, theme, etc...
  if (!from.name) {
    getUser().then((user) => {
      if (user && user.uid) {
        store.setLogin(user)
      }

      store.loadTheme(to.params.theme).then(() => {
        checkIfLoginRequired(to, next)
      })
    })
  } else {
    next()
  }
})

function checkIfLoginRequired(to, next) {
  const store = useRootStore()
  if (
    to.matched.some((record) => record.meta.requiresLogin) &&
    !store.loggedIn
  ) {
    store.setRedirectAfterLogin(to.fullPath)
    next({
      name: 'home'
    })
  } else {
    next()
  }
}

router.afterEach((to) => {
  // Use unhead for meta tags
  const defaultDescription =
    'Rijnja print, kopieert, scant, bewerkt en bezorgt documenten van alle formaten, op alle materialen. Van grootformaat tot kleinformaat, door heel Nederland.'
  const description = to.meta.description || defaultDescription
  const title =
    to.meta.title || 'Koninklijke Rijnja | Prints voor professionals'

  // Build canonical URL properly:
  // - Use route path without query params (to.path already excludes query)
  // - Remove trailing slash for consistency (except for home page)
  // - Always use lowercase
  let canonicalPath = to.path.toLowerCase()
  if (canonicalPath !== '/' && canonicalPath.endsWith('/')) {
    canonicalPath = canonicalPath.slice(0, -1)
  }
  const canonicalUrl = 'https://www.rijnja.nl' + canonicalPath

  // Determine if route should be indexed by search engines
  // Don't index: user-specific pages, checkout flows, search results, file operations
  const noindexRoutes = [
    'fileStore',
    'fileStoreHome',
    'fileStoreFolder',
    'fileStoreArchive',
    'fileStoreDashboard',
    'fileStoreTrash',
    'fileStoreTrashArchiveList',
    'fileStoreSearchResults',
    'printStoreSearchResults',
    'printOrder',
    'securedOrder',
    'printOrderCartV2',
    'printOrderDeliver',
    'printOrderInvoice',
    'checkOut',
    'checkOutDelivery',
    'checkOutInvoice',
    'checkOutProcess',
    'checkOutConfirm'
  ]
  const shouldNoindex = to.matched.some((route) =>
    noindexRoutes.includes(route.name)
  )

  const metaTags = [
    {
      name: 'description',
      content: description
    },
    {
      property: 'og:title',
      content: title
    },
    {
      property: 'og:description',
      content: description
    },
    {
      property: 'og:image',
      content: 'https://www.rijnja.nl/images/rijnja-social-share.jpg'
    },
    {
      property: 'og:type',
      content: 'website'
    },
    {
      property: 'og:url',
      content: canonicalUrl
    }
  ]

  // Add noindex for non-public pages
  if (shouldNoindex) {
    metaTags.push({
      name: 'robots',
      content: 'noindex, nofollow'
    })
  }

  useHead({
    title: title,
    meta: metaTags,
    link: [
      {
        rel: 'canonical',
        href: canonicalUrl
      }
    ]
  })
})

function redirectToHomeOnRefresh(to, from, next) {
  const store = useRootStore()
  if (!from.name) {
    next('home')
  } else {
    next()
  }
}

export default router
